import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms/button'
import { SettingSalesManagerTab } from '@/components/organisms/settings/sales-manager/tab'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { SubjectDetailModal } from '@/components/organisms/settings/sales-manager/subject-detail-modal'
import { LoaderContextCreator } from '@/contexts/loader'
import { EditFooter } from '@/components/organisms/edit-footer'
import { SalesListItem } from '@/components/organisms/settings/sales-manager/sales/sales-list-item'
import { deleteSalesSubject, exportSalesSubject, fetchSalesSubjectMaster } from '@/apis/aipass'
import { SalesSubjectMasterType } from '@/models/sales-manager/sales'
import { SubjectCsvUploadModal } from '@/components/organisms/settings/sales-manager/subject-csv-upload-modal'
import { Popup } from '@/components/atoms/popup'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'

const SalesListItemMemo = React.memo(SalesListItem)

export const SettingSalesManagerSales: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const [salesSettings, setSalesSettings] = useState<SalesSubjectMasterType[]>([])
  const [showDetailProp, setShowDetailProp] = useState<SalesSubjectMasterType | undefined | null>(undefined)
  const [deleteIds, setDeleteIds] = useState<string[]>([])
  const [nextDisplayOrder, setNextDisplayOrder] = useState<number>(1)
  const [showSubjectCsvModel, setShowSubjectCsvModel] = useState(false)
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()

  const initializeComponent = async (opt?: { noIndicator: boolean }) => {
    !opt?.noIndicator && setIsLoading(true)
    const salesSubjects = await fetchSalesSubjectMaster({ isIncludePlanCode: true })
    setSalesSettings([])
    setSalesSettings(salesSubjects || [])
    setDeleteIds([])
    !opt?.noIndicator && setIsLoading(false)
    setNextDisplayOrder(Math.max(0, ...salesSubjects.map(master => master.displayOrder || 1)) + 1)
  }

  const changeShowDetail = useCallback(
    (next: SalesSubjectMasterType | undefined | null) => {
      if (!guardSettingsEdit()) return
      setShowDetailProp(next)
    },
    [guardSettingsEdit],
  )

  const addDeleteTarget = useCallback(
    (id: string) => {
      if (!guardSettingsEdit()) return
      setDeleteIds([...deleteIds, id])
    },
    [deleteIds, guardSettingsEdit],
  )

  const doDelete = async () => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      if (deleteIds.length) {
        const hasRelatedMasterIds = salesSettings.filter(detail => detail.subSubjects.length).map(detail => detail.id)
        const isRelatedDeletion = deleteIds.some(deleteId => hasRelatedMasterIds.includes(deleteId))
        if (isRelatedDeletion) {
          if (!window.confirm(t('The related sub subjects will be deleted at the same time. \nDo you really want to delete them?'))) {
            return
          }
        }
        await deleteSalesSubject(deleteIds)
        await initializeComponent()
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const doDeleteCancel = () => {
    setDeleteIds([])
  }

  const downloadSaleDetails = async () => {
    try {
      setIsLoading(true)
      const { url } = await exportSalesSubject()
      window.open(url, '_blank')
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const isShowFooter = useMemo(() => deleteIds.length > 0, [deleteIds])
  const deleteFilteredSalesSubject = useMemo(() => salesSettings.filter(detail => !deleteIds.includes(detail.id)), [
    salesSettings,
    deleteIds,
  ])

  useEffect(() => {
    initializeComponent()
  }, [])

  const footerContent = isShowFooter ? <EditFooter onSave={doDelete} onCancel={doDeleteCancel} /> : undefined

  return (
    <SettingsLayout loading={false} footerContent={footerContent}>
      <TabContainer tabComponent={<SettingSalesManagerTab currentTab="Subject" />}>
        <>
          <div css={containerHeaderStyle}>
            <Button
              buttonType={isSettingsReadOnly ? 5 : 1}
              width={107}
              height={32}
              fontSize={12}
              isDisabled={isSettingsReadOnly}
              onClick={() => changeShowDetail(null)}
              icon={require('@/static/images/plus-white.svg')}
            >
              {t('Add new')}
            </Button>

            <div style={{ position: 'relative' }}>
              <Button
                buttonType={4}
                width={100}
                height={32}
                fontSize={12}
                icon={require('@/static/images/csv.svg')}
                onClick={() => setIsDropDownOpen(!isDropDownOpen)}
              >
                {t('CSV')} <img src={require('@/static/images/select_gray.svg')} style={{ marginLeft: 8 }} />
              </Button>
              <Popup isOpen={isDropDownOpen} onClose={() => setIsDropDownOpen(false)} customStyle={css({ width: 153, right: 0, top: 32 })}>
                <ul>
                  <li
                    css={dropDownContentStyle}
                    onClick={() => {
                      if (!guardSettingsEdit()) return
                      setIsDropDownOpen(false)
                      setShowSubjectCsvModel(true)
                    }}
                  >
                    <p>{t('Upload')}</p>
                  </li>
                  <li
                    css={dropDownContentStyle}
                    onClick={() => {
                      setIsDropDownOpen(false)
                      downloadSaleDetails()
                    }}
                  >
                    <p>{t('Download')}</p>
                  </li>
                </ul>
              </Popup>
            </div>
          </div>

          <SalesListItemMemo salesSettings={deleteFilteredSalesSubject} onClickRow={changeShowDetail} onClickDeleteRow={addDeleteTarget} />

          {showDetailProp !== undefined && (
            <SubjectDetailModal
              detail={showDetailProp}
              onSave={() => {
                initializeComponent()
                setShowDetailProp(undefined)
              }}
              onCancel={() => setShowDetailProp(undefined)}
              nextDisplayOrder={nextDisplayOrder}
            />
          )}
          {showSubjectCsvModel && (
            <SubjectCsvUploadModal
              onClose={() => setShowSubjectCsvModel(false)}
              onComplete={() => {
                setShowSubjectCsvModel(false)
                initializeComponent()
              }}
            />
          )}
        </>
      </TabContainer>
    </SettingsLayout>
  )
}

const containerHeaderStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '24px 32px 19px 32px',
  borderBottom: '1px solid #F2F2F2',
  '.subject-master-csv-uploader': { display: 'none' },
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
  'label, p': {
    fontSize: '14px !important',
    color: '#272727',
  },
})
